<template>
    <!-- 筛选栏 -->
    <el-card class="filter-card" shadow="never">
        <el-form :inline="true" :model="filters" class="filter-form">
            <el-form-item label="用户名">
                <el-input v-model="filters.name" placeholder="用户名" clearable />
            </el-form-item>

            <el-form-item>
                <el-button type="primary" @click="loadData">搜索</el-button>
                <el-button @click="resetFilters">重置</el-button>
                <el-divider direction="vertical" />
                <el-button type="primary" @click="addManager">新增</el-button>
            </el-form-item>
        </el-form>
    </el-card>

    <!-- 数据表格 -->
    <el-card class="table-card" shadow="never">
        <div class="table-wrapper">
            <el-table :data="rows" v-loading="loading" style="width: 100%">
                <el-table-column prop="name" label="Name" width="160">
                    <template #default="scope">
                        <el-link underline="always" type="primary" @click="editManager(scope.row)">{{ scope.row.name
                        }}</el-link>
                    </template>
                </el-table-column>
                <el-table-column label="操作" width="80">
                    <template #default="scope">
                        <el-link underline="always" type="info" @click="resetPwd(scope.row)">重置密码</el-link>
                    </template>
                </el-table-column>
                <el-table-column width="80">
                    <template #default="scope">
                        <el-link v-if="!scope.row.disabled" underline="always" type="danger"
                            @click="disableManager(scope.row)">禁用</el-link>
                        <el-link v-if="scope.row.disabled" underline="always" type="success"
                            @click="enableManager(scope.row)">启用</el-link>
                    </template>
                </el-table-column>
            </el-table>
        </div>
        <!-- 分页 -->
        <div class="pagination-wrapper">
            <el-pagination v-model:current-page="filters.page" v-model:page-size="filters.size"
                :page-sizes="[10, 20, 50]" :total="filters.total" layout="total, sizes, prev, pager, next, jumper"
                @current-change="loadData" @size-change="loadData" />
        </div>

        <el-dialog v-model="newManagerParams.showDialog" :title="newManagerParams.id ? '新增管理员' : '新增管理员'" width="400px">
            <el-form :model="newManagerParams" label-width="100px">
                <el-form-item label="名称" prop="name" required>
                    <el-input :disabled="!!newManagerParams.id" :readonly="!!newManagerParams.id"
                        v-model="newManagerParams.name" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="newManagerParams.showDialog = false">取消</el-button>
                <el-button v-loading="newManagerParams.loading" type="primary" @click="submit">确定</el-button>
            </template>
        </el-dialog>
    </el-card>
</template>

<script setup>
import { ref, reactive, watch, onMounted } from "vue";
import { useRoute, useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import { apiCall } from "@/utils/api";

const route = useRoute();
const router = useRouter();

const loading = ref(false);

const rows = ref([]);

const filters = reactive({
    name: route.query.name || "",

    page: Number(route.query.page) || 1,
    size: Number(route.query.size) || 20,
    total: 0,
});

watch(
    filters,
    (val) => {
        router.replace({ query: { ...val } });
    },
    { deep: true }
);

async function loadData() {
    loading.value = true;

    const params = {
        page: filters.page || 1,
        size: filters.size || 20,
    };
    if (filters.name) {
        params.name = filters.name;
    }

    const response = await apiCall("manager.getManagers", params);
    if (!response) {
        return false;
    }
    filters.total = response.count;
    rows.value = response.rows;

    loading.value = false;
}

async function resetFilters() {
    filters.name = "";
    filters.page = 1;
    await loadData();
}

const newManagerParams = reactive({
    showDialog: false,
    id: "",
    name: "",
    additionalAuth: [],
    loading: false,
});

function addManager() {
    newManagerParams.id = "";
    newManagerParams.name = "";
    newManagerParams.showDialog = true;
}

function editManager(row) {
    if (row.name === "admin") {
        return ElMessage.error("不能修改超级管理员");
    }
    newManagerParams.id = row.id;
    newManagerParams.name = row.name;
    newManagerParams.showDialog = true;
}

async function submit() {
    let success = false;
    if (!newManagerParams.id) {
        success = await submitAddManager();
    } else {
        success = await submitEditManager();
    }

    if (success) {
        newManagerParams.id = "";
        newManagerParams.name = "";
        newManagerParams.showDialog = false;
        await loadData();
    }
}

async function submitAddManager() {
    newManagerParams.loading = true;

    const response = await apiCall("manager.addManager", {
        name: newManagerParams.name,
    });
    newManagerParams.loading = false;
    if (!response) {
        return false;
    }
    ElMessage.success('增加管理员成功');
    return true;
}

async function submitEditManager() {
    newManagerParams.loading = true;

    const response = await apiCall("manager.modifyManager", {
        id: newManagerParams.id,
    });
    newManagerParams.loading = false;
    if (!response) {
        return false;
    }
    ElMessage.success('修改管理员成功');
    return true;
}

async function resetPwd(row) {
    ElMessageBox.confirm(
        `确定是否要重置管理员${row.name || ''}的密码？`,
        '确认',
        {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
        }
    )
        .then(async () => {
            loading.value = true;

            const response = await apiCall("manager.resetPwd", {
                id: row.id,
            });
            loading.value = false;
            if (!response) {
                return false;
            }
            ElMessage.success('重制密码成功，请通知相应管理员即刻登录并修改！');
        })
        .catch(() => { });
}

async function disableManager(row) {
    ElMessageBox.confirm(
        `确定是否要禁用管理员${row.name || ''}？`,
        '确认',
        {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
        }
    )
        .then(async () => {
            loading.value = true;

            const response = await apiCall("manager.disableManager", {
                id: row.id,
            });
            loading.value = false;
            if (!response) {
                return false;
            }
            row.disabled = true;
            ElMessage.success('管理员禁用成功！');
        })
        .catch(() => { });
}

async function enableManager(row) {
    ElMessageBox.confirm(
        `确定是否要启用管理员${row.name || ''}？`,
        '确认',
        {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning',
        }
    )
        .then(async () => {
            loading.value = true;

            const response = await apiCall("manager.enableManager", {
                id: row.id,
            });
            loading.value = false;
            if (!response) {
                return false;
            }
            row.disabled = false;
            ElMessage.success('启用管理员成功！');
        })
        .catch(() => { });
}

onMounted(async () => {
    await loadData();
});
</script>

<style scoped>
.role-select {
    width: 100px;
}
</style>
